import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export default function LiveCameraPage() {
  const location = useLocation();
  const navigate = useNavigate();

  // State
  const [passcode, setPasscode] = useState('');
  const [systemKey, setSystemKey] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [clientInfo, setClientInfo] = useState(null);
  const [cameras, setCameras] = useState([]);
  const [activeCamId, setActiveCamId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [currentSpeed, setCurrentSpeed] = useState(0.5);

  const videoContainerRef = useRef(null);
  const videoImgRef = useRef(null);

  const showToast = (msg) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(''), 3000);
  };

  const formatServerUrl = (key) => {
    let clean = key.trim();
    if (!clean) return 'http://localhost:8080';
    if (clean.startsWith('http://') || clean.startsWith('https://')) return clean.replace(/\/$/, '');
    if (clean.includes('localhost') || clean.includes('127.0.0.1') || clean.includes(':8080')) {
      return `http://${clean}`.replace(/\/$/, '');
    }
    if (!clean.includes('.')) return `https://${clean}.trycloudflare.com`;
    return `https://${clean}`.replace(/\/$/, '');
  };

  // Auto connect if URL has params (e.g. #k=xxx&code=739204 or ?k=xxx&code=739204)
  useEffect(() => {
    const hash = window.location.hash.substring(1);
    const hashParams = new URLSearchParams(hash);
    const queryParams = new URLSearchParams(location.search);

    const k = hashParams.get('k') || hashParams.get('key') || queryParams.get('k') || queryParams.get('key');
    const code = hashParams.get('code') || hashParams.get('passcode') || hashParams.get('c') || queryParams.get('code');

    const savedKey = localStorage.getItem('neko_sys_key') || '';
    if (k) {
      setSystemKey(k);
    } else if (savedKey) {
      setSystemKey(savedKey);
    }

    if (code) {
      setPasscode(code);
      if (k || savedKey) {
        handleLogin(code, k || savedKey);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const handleLogin = async (codeToUse, keyToUse) => {
    const c = (codeToUse || passcode).trim();
    const k = (keyToUse || systemKey).trim();

    if (!c) {
      alert('กรุณากรอกรหัสผ่านเข้าชมห้องน้องแมว');
      return;
    }

    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (!k && !isLocal) {
      alert('กรุณาระบุ System Key ของทางร้าน หรือเข้าสู่ระบบผ่านลิงก์/QR Code ที่ได้รับจากทางร้านครับ');
      return;
    }

    setIsLoading(true);
    const targetUrl = formatServerUrl(k);

    try {
      const res = await fetch(`${targetUrl}/api/auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: c }),
      });

      const data = await res.json();
      if (res.status !== 200 || data.status !== 'ok') {
        alert(data.message || 'รหัสผ่านไม่ถูกต้อง หรือถูกยกเลิกแล้ว');
        setIsLoading(false);
        return;
      }

      setClientInfo(data);
      const camList = data.cameras || [];
      setCameras(camList);
      if (camList.length > 0) {
        setActiveCamId(camList[0].id);
      }

      localStorage.setItem('neko_sys_key', k);
      setIsAuthenticated(true);
      showToast(`ยินดีต้อนรับ ${data.name}`);
    } catch (err) {
      console.error(err);
      alert('ไม่สามารถติดต่อเซิร์ฟเวอร์กล้องได้ กรุณาตรวจเช็ค System Key หรือตรวจสอบว่ากล้องที่ร้านเปิดอยู่');
    } finally {
      setIsLoading(false);
    }
  };

  // PTZ Control
  const handlePtz = (direction) => {
    if (!activeCamId) return;
    const targetUrl = formatServerUrl(systemKey);
    fetch(`${targetUrl}/api/ptz?cam_id=${activeCamId}&dir=${direction}&speed=${currentSpeed}`).catch(console.error);
  };

  const triggerDownload = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 2000);
  };

  const captureFromCanvas = () => {
    const img = videoImgRef.current;
    if (!img) {
      showToast('❌ ไม่พบภาพวิดีโอน้องแมว');
      return;
    }
    try {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth || img.clientWidth || 1280;
      canvas.height = img.naturalHeight || img.clientHeight || 720;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => {
        if (blob && blob.size > 1000) {
          triggerDownload(blob, `neko_${activeCamId}_${Date.now()}.jpg`);
          showToast('🎉 บันทึกภาพน้องแมวลงเครื่องเรียบร้อยแล้ว!');
        } else {
          showToast('❌ บันทึกภาพล้มเหลว');
        }
      }, 'image/jpeg', 0.95);
    } catch (err) {
      console.error(err);
      showToast('❌ บันทึกภาพล้มเหลว');
    }
  };

  // Snapshot & Download to user device
  const handleSnapshot = async () => {
    if (!activeCamId) return;
    showToast('📸 กำลังถ่ายรูปและดาวน์โหลด...');
    const targetUrl = formatServerUrl(systemKey);
    const filename = `neko_${activeCamId}_${Date.now()}.jpg`;

    // 1. ลองดึงภาพความละเอียดสูงสุดจาก Backend โดยตรง
    try {
      const downloadEndpoint = `${targetUrl}/api/snapshot?cam_id=${activeCamId}&download=true`;
      const res = await fetch(downloadEndpoint);
      const contentType = res.headers.get('content-type') || '';

      // ถ้าเซิร์ฟเวอร์ส่งไฟล์รูปภาพ JPEG กลับมา
      if (res.ok && contentType.startsWith('image/')) {
        const blob = await res.blob();
        if (blob && blob.size > 1000) {
          triggerDownload(blob, filename);
          showToast('🎉 บันทึกภาพความละเอียดสูงลงเครื่องเรียบร้อย!');
          return;
        }
      }

      // ถ้าเซิร์ฟเวอร์ส่ง JSON กลับมา (เช่น กรณี Backend ยังไม่ได้รีสตาร์ท)
      if (contentType.includes('application/json')) {
        const data = await res.json();
        if (data.status === 'ok' && data.filename) {
          try {
            const fileRes = await fetch(`${targetUrl}/snapshots/${data.filename}`);
            const fileContentType = fileRes.headers.get('content-type') || '';
            if (fileRes.ok && fileContentType.startsWith('image/')) {
              const imgBlob = await fileRes.blob();
              triggerDownload(imgBlob, filename);
              showToast('🎉 บันทึกภาพลงเครื่องเรียบร้อยแล้ว!');
              return;
            }
          } catch (e) {
            // ไม่ผ่าน ให้แคปเจอร์จากหน้าจอวิดีโอทันที
          }
        }
      }
    } catch (err) {
      console.warn('Backend snapshot fetch failed, falling back to direct video capture', err);
    }

    // 2. Fallback: แคปเจอร์จากหน้าจอวิดีโอ (Canvas) ทันที ได้ภาพ 100% แน่นอน
    captureFromCanvas();
  };

  // Fullscreen
  const toggleFullscreen = () => {
    if (!videoContainerRef.current) return;
    if (!document.fullscreenElement) {
      videoContainerRef.current.requestFullscreen().catch(console.error);
    } else {
      document.exitFullscreen();
    }
  };

  const handleExit = () => {
    if (window.confirm('ต้องการออกจากระบบดูกล้องใช่หรือไม่?')) {
      setIsAuthenticated(false);
      setClientInfo(null);
      setCameras([]);
      setActiveCamId('');
    }
  };

  const targetServer = formatServerUrl(systemKey);
  const activeCamera = cameras.find((c) => c.id === activeCamId) || cameras[0];

  return (
    <div className="min-h-screen bg-[#FFF8EF] text-[#3A2B1C] font-sans flex flex-col">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-[#F2E5D5] px-4 py-3 flex justify-between items-center sticky top-0 z-50 shadow-sm">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/')}
            className="text-sm font-medium text-[#7C482C] hover:text-[#FFA726] flex items-center gap-1"
          >
            ← กลับหน้าแรก
          </button>
          <div className="h-4 w-px bg-gray-300"></div>
          <div className="flex items-center gap-2">
            <span className="text-xl">🐱</span>
            <span className="font-bold text-lg text-[#3A2B1C]">Neko Guru Live Cam</span>
          </div>
        </div>

        {isAuthenticated && (
          <div className="flex items-center gap-3">
            <span className="text-xs bg-[#FFF3D3] text-[#7C482C] font-semibold px-3 py-1 rounded-full border border-[#FFA726]">
              {clientInfo?.name || 'ผู้เข้าชม'}
            </span>
            <button
              onClick={handleExit}
              className="text-xs text-red-600 hover:text-red-700 font-medium border border-red-200 px-2.5 py-1 rounded-lg"
            >
              ออกจากระบบ
            </button>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col justify-center items-center p-4 max-w-6xl w-full mx-auto">
        {!isAuthenticated ? (
          /* --- หน้าจอเข้าสู่ระบบ / กรอกรหัส --- */
          <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-xl border border-[#F2E5D5] text-center my-8">
            <div className="w-20 h-20 bg-[#FFF3D3] text-[#FFA726] rounded-full flex items-center justify-center text-4xl mx-auto mb-4 border-2 border-[#FFA726]/40 shadow-inner">
              🐾
            </div>

            <h2 className="text-2xl font-bold text-[#3A2B1C] mb-2">เข้าชมห้องน้องแมวสด</h2>
            <p className="text-sm text-[#7C482C]/80 mb-6">
              กรุณากรอกรหัสผ่าน 6 หลัก หรือสแกน QR Code ที่ได้รับจากทางร้าน เพื่อส่องดูน้องแมวได้ตลอด 24 ชม.
            </p>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleLogin();
              }}
              className="space-y-4 text-left"
            >
              <div>
                <label className="block text-xs font-semibold text-[#7C482C] mb-1.5 text-center">
                  รหัสผ่านเข้าชม (Passcode 6 หลัก):
                </label>
                <input
                  type="text"
                  value={passcode}
                  onChange={(e) => setPasscode(e.target.value)}
                  placeholder="เช่น 739204 หรือ 8888"
                  className="w-full bg-[#FFF8EF] border-2 border-[#FFA726]/40 focus:border-[#FFA726] rounded-2xl p-3.5 text-center text-xl font-mono tracking-widest text-[#3A2B1C] outline-none shadow-sm transition"
                  required
                />
              </div>

              <div>
                <label className="block text-[11px] text-gray-500 mb-1 text-center">
                  System Key (รหัสระบุสาขา/ร้าน):
                </label>
                <input
                  type="text"
                  value={systemKey}
                  onChange={(e) => setSystemKey(e.target.value)}
                  placeholder="หากเปิดจากลิงก์หรือสแกน QR Code จะกรอกให้อัตโนมัติ"
                  className="w-full bg-[#FFF8EF] border border-[#F2E5D5] rounded-xl p-2 text-xs text-center text-gray-700 outline-none"
                />
                <p className="text-[10px] text-gray-400 text-center mt-1">
                  * หากเปิดจาก QR Code / ลิงก์ของร้าน ระบบจะกรอกให้อัตโนมัติ (ไม่ต้องกรอกเอง)
                </p>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-[#FFA726] hover:bg-[#ff9800] text-white font-bold py-3.5 rounded-2xl shadow-md hover:shadow-lg transition transform active:scale-95 text-base mt-2"
              >
                {isLoading ? 'กำลังเชื่อมต่อ...' : '▶ เข้าชมห้องน้องแมวสด'}
              </button>
            </form>

            <div className="mt-6 pt-4 border-t border-[#F2E5D5] text-xs text-gray-500">
              🐈 ส่องดูพฤติกรรมและความเป็นอยู่ของน้องแมวได้แบบเรียลไทม์
            </div>
          </div>
        ) : (
          /* --- หน้าจอรับชมกล้องสด & ควบคุม PTZ --- */
          <div className="w-full grid grid-cols-1 lg:grid-cols-3 gap-6 my-4">
            {/* กล่องวิดีโอ (2 ส่วน) */}
            <div className="lg:col-span-2 bg-white rounded-3xl overflow-hidden shadow-xl border border-[#F2E5D5] flex flex-col">
              <div ref={videoContainerRef} className="relative bg-black aspect-video w-full flex items-center justify-center overflow-hidden">
                <img
                  ref={videoImgRef}
                  crossOrigin="anonymous"
                  src={`${targetServer}/video_feed/${activeCamId}?t=${Date.now()}`}
                  alt="Live Camera Feed"
                  className="w-full h-full object-contain"
                />
                <div className="absolute top-3 left-3 bg-black/60 backdrop-blur-md px-3 py-1 rounded-full text-white text-xs flex items-center gap-2 border border-white/10">
                  <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
                  <span>{activeCamera?.name || 'ห้องน้องแมว'} • LIVE</span>
                </div>
              </div>

              <div className="p-4 bg-[#FFF8EF] flex justify-between items-center border-t border-[#F2E5D5]">
                <button
                  onClick={handleSnapshot}
                  className="px-4 py-2 bg-white hover:bg-[#FFF3D3] text-[#3A2B1C] font-semibold text-sm rounded-xl border border-[#F2E5D5] shadow-sm flex items-center gap-1.5 transition"
                >
                  📸 ถ่ายรูปน้องแมว (Snapshot)
                </button>
                <button
                  onClick={toggleFullscreen}
                  className="px-4 py-2 bg-white hover:bg-[#FFF3D3] text-[#3A2B1C] font-semibold text-sm rounded-xl border border-[#F2E5D5] shadow-sm flex items-center gap-1.5 transition"
                >
                  ⛶ ขยายเต็มจอ
                </button>
              </div>
            </div>

            {/* แผงควบคุม PTZ และตัวเลือกห้อง (1 ส่วน) */}
            <div className="bg-white rounded-3xl p-6 shadow-xl border border-[#F2E5D5] flex flex-col gap-6">
              <div className="border-b border-[#F2E5D5] pb-3">
                <h3 className="font-bold text-lg text-[#3A2B1C] flex items-center gap-2">
                  <span>🎮 ควบคุมมุมกล้อง</span>
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">หันซ้าย-ขวา ก้ม-เงย ส่องดูน้องแมว</p>
              </div>

              {/* สลับห้อง (กรณีได้สิทธิ์หลายห้อง) */}
              {cameras.length > 1 && (
                <div>
                  <label className="block text-xs font-semibold text-[#7C482C] mb-1.5">เลือกห้องน้องแมว:</label>
                  <select
                    value={activeCamId}
                    onChange={(e) => setActiveCamId(e.target.value)}
                    className="w-full bg-[#FFF8EF] border border-[#F2E5D5] rounded-xl p-2.5 text-sm font-semibold text-[#3A2B1C] outline-none"
                  >
                    {cameras.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* D-Pad จอยสติ๊กสั่งหมุนกล้อง */}
              <div className="flex justify-center items-center my-2">
                <div className="relative w-52 h-52 bg-[#FFF8EF] rounded-full border-4 border-[#FFA726]/40 shadow-inner grid grid-cols-3 grid-rows-3 gap-2 p-3">
                  <div></div>
                  <button
                    onPointerDown={() => handlePtz('up')}
                    onPointerUp={() => handlePtz('stop')}
                    className="bg-white hover:bg-[#FFA726] active:bg-[#ff9800] active:text-white text-[#7C482C] font-bold rounded-2xl shadow border border-[#F2E5D5] flex items-center justify-center text-xl transition"
                  >
                    ▲
                  </button>
                  <div></div>

                  <button
                    onPointerDown={() => handlePtz('left')}
                    onPointerUp={() => handlePtz('stop')}
                    className="bg-white hover:bg-[#FFA726] active:bg-[#ff9800] active:text-white text-[#7C482C] font-bold rounded-2xl shadow border border-[#F2E5D5] flex items-center justify-center text-xl transition"
                  >
                    ◀
                  </button>
                  <button
                    onClick={() => handlePtz('stop')}
                    className="bg-[#7C482C] text-white rounded-full flex items-center justify-center text-xs font-bold shadow hover:bg-black transition"
                    title="หยุด"
                  >
                    ⏹
                  </button>
                  <button
                    onPointerDown={() => handlePtz('right')}
                    onPointerUp={() => handlePtz('stop')}
                    className="bg-white hover:bg-[#FFA726] active:bg-[#ff9800] active:text-white text-[#7C482C] font-bold rounded-2xl shadow border border-[#F2E5D5] flex items-center justify-center text-xl transition"
                  >
                    ▶
                  </button>

                  <div></div>
                  <button
                    onPointerDown={() => handlePtz('down')}
                    onPointerUp={() => handlePtz('stop')}
                    className="bg-white hover:bg-[#FFA726] active:bg-[#ff9800] active:text-white text-[#7C482C] font-bold rounded-2xl shadow border border-[#F2E5D5] flex items-center justify-center text-xl transition"
                  >
                    ▼
                  </button>
                  <div></div>
                </div>
              </div>

              {/* ความเร็วการหมุน */}
              <div>
                <div className="flex justify-between text-xs text-[#7C482C] mb-1 font-semibold">
                  <span>ความเร็วในการหมุน:</span>
                  <span>{Math.round(currentSpeed * 100)}%</span>
                </div>
                <input
                  type="range"
                  min="20"
                  max="100"
                  value={currentSpeed * 100}
                  onChange={(e) => setCurrentSpeed(e.target.value / 100)}
                  className="w-full accent-[#FFA726] cursor-pointer"
                />
              </div>

              <div className="bg-[#FFF8EF] rounded-2xl p-3 border border-[#F2E5D5] text-[11px] text-gray-600 leading-relaxed text-center">
                💡 แตะค้างที่ปุ่มทิศทางเพื่อหมุนกล้อง และปล่อยมือเพื่อหยุด
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Toast Notification */}
      {toastMessage && (
        <div className="fixed bottom-6 right-6 bg-[#3A2B1C] text-white px-5 py-3 rounded-2xl shadow-xl text-sm font-medium z-50 animate-bounce">
          {toastMessage}
        </div>
      )}
    </div>
  );
}
